import classNames from 'classnames';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import CloseIcon, { SIZES } from 'components/CloseIcon';
import { SearchIcon, SearchWhiteIcon } from 'components/Icon';
import IconButton from 'components/IconButton';
import Input, { TYPES } from 'components/Input';
import { extraLarge } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import { queryParamSearchQuery } from 'utils/Url';

const SEARCH_ICON_SIZE = 13;
const CLOSE_ICON_SIZE = 18;

const SearchBarMinimal = ({ value, isInverted, onSubmit, disableOnSubmit, onChange, onClear }) => {
  const [valueState, setValueState] = useState(value);
  const ref = useRef(null);

  useEffect(() => {
    const query = queryParamSearchQuery();

    if (query && !valueState) {
      setValueState(decodeURIComponent(query));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce(valueDebounced => {
      onChange(valueDebounced);
    }, 500),
    []
  );

  const onChangeEvent = event => {
    const { value } = event.target;

    setValueState(value);
    debouncedOnChange(value);
  };

  const onClickEvent = () => {
    onClear();
    setValueState('');
  };

  return (
    <div
      className={classNames('SearchBarMinimal', {
        'SearchBarMinimal--inverted': isInverted,
      })}
    >
      <form
        className="SearchBarMinimal__form"
        onSubmit={e => {
          e.preventDefault();
          if (!disableOnSubmit) {
            ref.current.blur();
            onSubmit(value);
          }
        }}
      >
        <div className="SearchBarMinimal__input-container">
          <Input
            inputType="search"
            id="SearchBarMinimal__input"
            className="SearchBarMinimal__input"
            onChange={onChangeEvent}
            ariaLabel={I18n.localize('searchBar.enterSearchTerm')}
            placeholder={I18n.localize('searchBar.enterSearchTerm')}
            value={valueState}
            type={isInverted ? TYPES.INVERSE : null}
            forwardedRef={ref}
            autoComplete="off"
            name="query"
          />
          <div className="SearchBarMinimal__icon-left">
            <IconButton
              className="SearchBarMinimal__search-button"
              onClick={() => onSubmit(value)}
              aria-label={I18n.localize('searchBar.search')}
              type="button"
            >
              {isInverted ? (
                <SearchWhiteIcon className="SearchBarMinimal__search-icon" />
              ) : (
                <SearchIcon className="SearchBarMinimal__search-icon" />
              )}
            </IconButton>
          </div>
          <div className="SearchBarMinimal__icon-right">
            {valueState !== '' && (
              <IconButton
                className="SearchBarMinimal__clear-button"
                onClick={onClickEvent}
                aria-label={I18n.localize('searchBar.clear')}
                type="button"
              >
                <CloseIcon
                  className="SearchBarMinimal__clear-icon"
                  size={SIZES.SMALL}
                  disableHoverAnimation
                />
              </IconButton>
            )}
          </div>
        </div>
      </form>

      <style jsx>{`
        .SearchBarMinimal {
          position: relative;
        }

        .SearchBarMinimal__input-container {
          width: 100%;
        }

        .SearchBarMinimal__icon-left {
          min-width: ${Theme.get('spacing') * 6}px;
          min-height: ${Theme.get('spacing') * 6}px;
          position: absolute;
          top: 0;
          left: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        .SearchBarMinimal__icon-right {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: ${Theme.get('spacing') * 2}px;
        }
      `}</style>
      <style jsx global>{`
        .SearchBarMinimal__input.Input {
          font-weight: ${Theme.get('font-weight-normal')};
          padding: 0 ${Theme.get('spacing') * 6}px;
          border: none;
          transition: none;
          border-radius: ${Theme.get('font-size-small')}px;
          line-height: 1;
        }

        .SearchBarMinimal__input.Input::placeholder {
          color: ${Theme.get('colors-black-60')};
        }

        .SearchBarMinimal__input.Input:active,
        .SearchBarMinimal__input.Input:focus {
          box-shadow: none;
        }

        .SearchBarMinimal .SearchBarMinimal__clear-icon.CloseIcon:after,
        .SearchBarMinimal .SearchBarMinimal__clear-icon.CloseIcon:before {
          background-color: ${Theme.get('colors-black-60')};
          height: ${CLOSE_ICON_SIZE}px;
        }

        .SearchBarMinimal--inverted .SearchBarMinimal__clear-icon.CloseIcon:after,
        .SearchBarMinimal--inverted .SearchBarMinimal__clear-icon.CloseIcon:before {
          background-color: ${Theme.get('colors-white')};
          height: ${CLOSE_ICON_SIZE}px;
        }

        .IconButton.SearchBarMinimal__clear-button {
          min-height: auto;
          min-width: auto;
          margin: 0;
          padding: ${Theme.get('spacing')}px;
        }

        .IconButton.SearchBarMinimal__search-button {
          min-width: ${Theme.get('spacing') * 8}px;
          min-height: ${Theme.get('spacing') * 8}px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
        }

        .SearchBox .SearchBarMinimal__icon-left {
          min-width: ${Theme.get('spacing') * 8}px;
          min-height: ${Theme.get('spacing') * 8}px;
        }

        .SearchBox .Input.SearchBarMinimal__input {
          padding-left: ${Theme.get('spacing') * 7}px;
        }

        @media ${extraLarge} {
          .IconButton.SearchBarMinimal__search-button {
            min-width: ${Theme.get('spacing') * 6}px;
            min-height: ${Theme.get('spacing') * 6}px;
          }
        }

        .Icon.SearchBarMinimal__search-icon {
          min-height: auto;
          min-width: auto;
          padding: 0;
        }

        .Icon.SearchBarMinimal__search-icon svg {
          height: ${SEARCH_ICON_SIZE}px;
          width: ${SEARCH_ICON_SIZE}px;
        }
      `}</style>
    </div>
  );
};

SearchBarMinimal.propTypes = {
  isInverted: PropTypes.bool,
  value: PropTypes.string,
  onClear: PropTypes.func,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  disableOnSubmit: PropTypes.bool,
};

SearchBarMinimal.defaultProps = {
  value: '',
  isInverted: false,
  onSubmit: () => {},
  onChange: () => {},
  onClear: () => {},
  disableOnSubmit: false,
};

export default SearchBarMinimal;
